import { Levels, OrthoTileModel } from '../../../../../typings/api/skymap/rest/v0/.common';
import { OrthoTileStore } from '../../../stores/geodata-store-factory/ortho-tile-store/ortho-tile-store';
import { t } from '../../../utils/i18-next';
import { isDefined } from '../../../utils/variables';
import { GlobalMercator } from '../../utilities/projections/global-mercator';

/** Estimate pixels at a level given area (m²), tile size (px), meters/tile at zoom. */
function estimatePixelsForLevel(
  areaWidthMeters: number,
  areaHeightMeters: number,
  tileSizePx: number,
  tileToMeterWidthAtZoom: number,
): number {
  const pxPerMeter = tileSizePx / tileToMeterWidthAtZoom;
  return areaWidthMeters * areaHeightMeters * pxPerMeter * pxPerMeter;
}

/** Pick the highest zoom that keeps the pixel count <= maxPixels (fallback to lowest). */
function selectBestLevel(
  orthotile: OrthoTileModel,
  globalMercator: GlobalMercator,
  areaWidthMeters: number,
  areaHeightMeters: number,
  maxPixels: number,
): Levels {
  // iterate high->low
  for (let i = orthotile.levels.length - 1; i >= 0; i--) {
    const level = orthotile.levels[i];
    const metersPerTile = globalMercator.tileToMeterWidth(level.zoom);
    const est = estimatePixelsForLevel(
      areaWidthMeters,
      areaHeightMeters,
      orthotile.tileSize,
      metersPerTile,
    );
    if (est <= maxPixels) {
      return level;
    }
  }
  return orthotile.levels[0];
}

export interface LocalBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Convert local bounds to WGS84 (lon/lat) and normalize to (lonMin, lonMax, latMin, latMax). */
function localBoundsToWgs84Normalized(
  bounds: LocalBounds,
  converter: { convert: (x: number, y: number) => { lon: number; lat: number } },
): { lonMin: number; lonMax: number; latMin: number; latMax: number } {
  // Convert all four corners — projecting only the two diagonal corners produces
  // a skewed envelope for northern latitudes where Web Mercator is non-linear.
  const bl = converter.convert(bounds.minX, bounds.minY);
  const br = converter.convert(bounds.maxX, bounds.minY);
  const tl = converter.convert(bounds.minX, bounds.maxY);
  const tr = converter.convert(bounds.maxX, bounds.maxY);
  const lonMin = Math.min(bl.lon, br.lon, tl.lon, tr.lon);
  const lonMax = Math.max(bl.lon, br.lon, tl.lon, tr.lon);
  const latMin = Math.min(bl.lat, br.lat, tl.lat, tr.lat);
  const latMax = Math.max(bl.lat, br.lat, tl.lat, tr.lat);
  return { lonMin, lonMax, latMin, latMax };
}
function clampLat(lat: number): number {
  return Math.max(Math.min(lat, 85.05112878), -85.05112878);
}

function lonLatToTileFloat(lon: number, lat: number, zoom: number): { tx: number; ty: number } {
  const n = Math.pow(2, zoom);
  const x = ((lon + 180) / 360) * n;
  const phi = (clampLat(lat) * Math.PI) / 180;
  const y = ((1 - Math.log(Math.tan(phi) + 1 / Math.cos(phi)) / Math.PI) / 2) * n;
  return { tx: x, ty: y };
}

function lonLatToGlobalPixels(
  lon: number,
  lat: number,
  zoom: number,
  tileSize: number,
): { px: number; py: number } {
  const { tx, ty } = lonLatToTileFloat(lon, lat, zoom);
  return { px: tx * tileSize, py: ty * tileSize }; // y grows downward
}

/** Compute exact pixel window (inclusive/exclusive) for the WGS84 bounds. */
function computePixelWindow(
  lonMin: number,
  latMax: number,
  lonMax: number,
  latMin: number,
  zoom: number,
  tileSize: number,
): { pxMin: number; pyMin: number; pxMax: number; pyMax: number; width: number; height: number } {
  const ul = lonLatToGlobalPixels(lonMin, latMax, zoom, tileSize); // upper-left
  const lr = lonLatToGlobalPixels(lonMax, latMin, zoom, tileSize); // lower-right

  const pxMin = Math.floor(ul.px);
  const pyMin = Math.floor(ul.py);
  const pxMax = Math.ceil(lr.px);
  const pyMax = Math.ceil(lr.py);
  const width = pxMax - pxMin;
  const height = pyMax - pyMin;
  if (width <= 0 || height <= 0) {
    throw new Error('Non-positive output dimensions. Check bounds ordering.');
  }
  return { pxMin, pyMin, pxMax, pyMax, width, height };
}

/** Integer tile range needed for a pixel window. */
function pixelWindowToTileRange(
  pxMin: number,
  pyMin: number,
  pxMax: number,
  pyMax: number,
  tileSize: number,
): { txMin: number; tyMin: number; txMax: number; tyMax: number } {
  const txMin = Math.floor(pxMin / tileSize);
  const tyMin = Math.floor(pyMin / tileSize);
  const txMax = Math.floor((pxMax - 1) / tileSize);
  const tyMax = Math.floor((pyMax - 1) / tileSize);
  return { txMin, tyMin, txMax, tyMax };
}

/** Clamp a tile range to dataset availability. */
function clampTileRange(
  range: { txMin: number; tyMin: number; txMax: number; tyMax: number },
  level: Levels,
): { txMin: number; tyMin: number; txMax: number; tyMax: number } {
  const txMin = Math.max(range.txMin, level.columnMin);
  const txMax = Math.min(range.txMax, level.columnMax);
  const tyMin = Math.max(range.tyMin, level.rowMin);
  const tyMax = Math.min(range.tyMax, level.rowMax);
  if (txMin > txMax || tyMin > tyMax) {
    throw new Error('Requested bounds outside available tiles for this level.');
  }
  return { txMin, tyMin, txMax, tyMax };
}

interface DrawSegment {
  zoom: number;
  tileX: number;
  tileY: number;
  srcX: number;
  srcY: number;
  width: number;
  height: number; // from the tile image
  dstX: number;
  dstY: number; // to the output canvas
}

/**
 * Build a list of tile draw segments that exactly fill the pixel window.
 * No IO, no canvas — just math you can snapshot-test.
 */
function buildDrawPlan(
  zoom: number,
  tileSize: number,
  pxMin: number,
  pyMin: number,
  pxMax: number,
  pyMax: number,
  clamped: { txMin: number; tyMin: number; txMax: number; tyMax: number },
): DrawSegment[] {
  const plan: DrawSegment[] = [];
  for (let tx = clamped.txMin; tx <= clamped.txMax; tx++) {
    for (let ty = clamped.tyMin; ty <= clamped.tyMax; ty++) {
      const tileOriginX = tx * tileSize;
      const tileOriginY = ty * tileSize;

      const destX = tileOriginX - pxMin;
      const destY = tileOriginY - pyMin;

      const srcX = Math.max(0, pxMin - tileOriginX);
      const srcY = Math.max(0, pyMin - tileOriginY);

      const dstX = Math.max(0, destX);
      const dstY = Math.max(0, destY);

      const drawW = Math.min(tileSize - srcX, pxMax - pxMin - dstX);
      const drawH = Math.min(tileSize - srcY, pyMax - pyMin - dstY);

      if (drawW > 0 && drawH > 0) {
        plan.push({
          zoom,
          tileX: tx,
          tileY: ty,
          srcX,
          srcY,
          width: drawW,
          height: drawH,
          dstX,
          dstY,
        });
      }
    }
  }
  return plan;
}

/** Load an image from a URL (http(s) or data URL). Use for drawing tiles to canvas. */
function loadTileImageFromUrl(url: string): Promise<HTMLImageElement> {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  return new Promise((resolve, reject) => {
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

/** Draw a single segment: tiny wrapper around ctx.drawImage. */
function drawSegment(ctx: CanvasRenderingContext2D, img: HTMLImageElement, seg: DrawSegment) {
  ctx.drawImage(
    img,
    seg.srcX,
    seg.srcY,
    seg.width,
    seg.height,
    seg.dstX,
    seg.dstY,
    seg.width,
    seg.height,
  );
}

/**
 * Build a sparse grid of Web Mercator pixel offsets for bilinear reprojection.
 * Only (gridN+1)² converter calls are needed regardless of output resolution.
 */
function buildReprojectionGrid(
  bounds: LocalBounds,
  converter: { convert: (x: number, y: number) => { lon: number; lat: number } },
  zoom: number,
  tileSize: number,
  pxMin: number,
  pyMin: number,
  gridN: number,
): { gridX: Float32Array; gridY: Float32Array } {
  const gridX = new Float32Array((gridN + 1) * (gridN + 1));
  const gridY = new Float32Array((gridN + 1) * (gridN + 1));
  const localWidth = bounds.maxX - bounds.minX;
  const localHeight = bounds.maxY - bounds.minY;

  for (let gy = 0; gy <= gridN; gy++) {
    for (let gx = 0; gx <= gridN; gx++) {
      const localX = bounds.minX + (gx / gridN) * localWidth;
      const localY = bounds.maxY - (gy / gridN) * localHeight; // local Y up → canvas Y down
      const { lon, lat } = converter.convert(localX, localY);
      const { px, py } = lonLatToGlobalPixels(lon, lat, zoom, tileSize);
      const idx = gy * (gridN + 1) + gx;
      gridX[idx] = px - pxMin;
      gridY[idx] = py - pyMin;
    }
  }
  return { gridX, gridY };
}

/**
 * Remap srcCanvas (Web Mercator pixel space) into a new canvas aligned to local
 * coordinate space via bilinear interpolation of the reprojection grid.
 */
function remapMercatorToLocal(
  srcCanvas: HTMLCanvasElement,
  outW: number,
  outH: number,
  gridN: number,
  gridX: Float32Array,
  gridY: Float32Array,
): HTMLCanvasElement {
  const srcCtx = srcCanvas.getContext('2d');
  if (!srcCtx) {
    throw new Error('No 2D context');
  }
  const srcData = srcCtx.getImageData(0, 0, srcCanvas.width, srcCanvas.height);
  const srcW = srcCanvas.width;
  const srcH = srcCanvas.height;

  const outCanvas = document.createElement('canvas');
  outCanvas.width = outW;
  outCanvas.height = outH;
  const outCtx = outCanvas.getContext('2d');
  if (!outCtx) {
    throw new Error('No 2D context');
  }
  const outData = outCtx.createImageData(outW, outH);
  const stride = gridN + 1;

  for (let row = 0; row < outH; row++) {
    const tRow = row / outH;
    const gy0 = Math.min(Math.floor(tRow * gridN), gridN - 1);
    const gy1 = gy0 + 1;
    const ty = tRow * gridN - gy0;

    for (let col = 0; col < outW; col++) {
      const tCol = col / outW;
      const gx0 = Math.min(Math.floor(tCol * gridN), gridN - 1);
      const gx1 = gx0 + 1;
      const tx = tCol * gridN - gx0;

      const w00 = (1 - tx) * (1 - ty);
      const w10 = tx * (1 - ty);
      const w01 = (1 - tx) * ty;
      const w11 = tx * ty;

      const i00 = gy0 * stride + gx0;
      const i10 = gy0 * stride + gx1;
      const i01 = gy1 * stride + gx0;
      const i11 = gy1 * stride + gx1;

      const srcPx = gridX[i00] * w00 + gridX[i10] * w10 + gridX[i01] * w01 + gridX[i11] * w11;
      const srcPy = gridY[i00] * w00 + gridY[i10] * w10 + gridY[i01] * w01 + gridY[i11] * w11;

      const srcCol = Math.round(srcPx);
      const srcRow = Math.round(srcPy);

      if (srcCol < 0 || srcCol >= srcW || srcRow < 0 || srcRow >= srcH) {
        continue;
      }

      const srcIdx = (srcRow * srcW + srcCol) * 4;
      const dstIdx = (row * outW + col) * 4;
      outData.data[dstIdx] = srcData.data[srcIdx];
      outData.data[dstIdx + 1] = srcData.data[srcIdx + 1];
      outData.data[dstIdx + 2] = srcData.data[srcIdx + 2];
      outData.data[dstIdx + 3] = srcData.data[srcIdx + 3];
    }
  }

  outCtx.putImageData(outData, 0, 0);
  return outCanvas;
}

async function downloadHighResolutionOrthotile(
  bounds: LocalBounds,
  orthotile: OrthoTileModel,
  globalMercator: GlobalMercator,
  converter: { convert: (x: number, y: number) => { lon: number; lat: number } },
  store: OrthoTileStore,
  maxPixels = 10_000_000,
  tileSizeOverride?: number,
  onProgress?: (progress: number, label?: string) => void,
): Promise<HTMLCanvasElement> {
  const level = selectBestLevel(
    orthotile,
    globalMercator,
    bounds.maxX - bounds.minX,
    bounds.maxY - bounds.minY,
    maxPixels,
  );
  const zoom = level.zoom;

  const TILE = tileSizeOverride ?? orthotile.tileSize;

  const { lonMin, lonMax, latMin, latMax } = localBoundsToWgs84Normalized(bounds, converter);

  const win = computePixelWindow(lonMin, latMax, lonMax, latMin, zoom, TILE);
  const range = pixelWindowToTileRange(win.pxMin, win.pyMin, win.pxMax, win.pyMax, TILE);
  const clamped = clampTileRange(range, level);

  const plan = buildDrawPlan(zoom, TILE, win.pxMin, win.pyMin, win.pxMax, win.pyMax, clamped);

  // Step 1: composite tiles onto an intermediate Web Mercator canvas.
  const tileCanvas = document.createElement('canvas');
  tileCanvas.width = win.width;
  tileCanvas.height = win.height;
  const ctx = tileCanvas.getContext('2d');
  if (!ctx) {
    throw new Error('No 2D context');
  }

  let detectedTileSize: number | undefined;
  let processedTiles = 0;
  const totalTiles = plan.length;
  const label = t('cadDrawingReport.downloadingOrthophoto', { ns: 'skyviewReport' });

  for (const seg of plan) {
    if (onProgress) {
      onProgress(processedTiles / totalTiles, label);
      processedTiles++;
    }

    const url = await store.loadTileUrl(seg.zoom, seg.tileX, seg.tileY);
    let img: HTMLImageElement;
    try {
      img = await loadTileImageFromUrl(url);
    } catch (e) {
      console.warn(`Failed to load tile ${seg.tileX}/${seg.tileY} at z${seg.zoom}:`, e);
      continue;
    }

    if (detectedTileSize === undefined) {
      detectedTileSize = img.width;
      if (detectedTileSize !== TILE && !isDefined(tileSizeOverride)) {
        console.warn('Tile size mismatch: metadata', TILE, 'vs image', detectedTileSize);
        return await downloadHighResolutionOrthotile(
          bounds,
          orthotile,
          globalMercator,
          converter,
          store,
          maxPixels,
          detectedTileSize,
          onProgress,
        );
      }
    }

    drawSegment(ctx, img, seg);
  }

  // Step 2: remap to local coordinate space so the canvas exactly represents the
  // local rectangle without Web Mercator projection distortion.
  const metersPerTile = globalMercator.tileToMeterWidth(zoom);
  const pxPerMeter = TILE / metersPerTile;
  const outW = Math.max(1, Math.round((bounds.maxX - bounds.minX) * pxPerMeter));
  const outH = Math.max(1, Math.round((bounds.maxY - bounds.minY) * pxPerMeter));

  const GRID_N = 64;
  const { gridX, gridY } = buildReprojectionGrid(
    bounds,
    converter,
    zoom,
    TILE,
    win.pxMin,
    win.pyMin,
    GRID_N,
  );
  return remapMercatorToLocal(tileCanvas, outW, outH, GRID_N, gridX, gridY);
}

export const composeOrthotileHelpers = {
  downloadHighResolutionOrthotile,
  loadTileImageFromUrl,
  drawSegment,
  estimatePixelsForLevel,
  selectBestLevel,
  localBoundsToWgs84Normalized,
  lonLatToTileFloat,
  lonLatToGlobalPixels,
  computePixelWindow,
  pixelWindowToTileRange,
  clampTileRange,
  buildDrawPlan,
  buildReprojectionGrid,
  remapMercatorToLocal,
};
